import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomersService } from '../../../../services/customers.service';
import { Customer } from '../../../../models/Customer';
import { CustomerContact } from '../../../../models/Customer';
import { CustomerAttachment } from '../../../../models/CustomerAttachment';
import { FormShellTab } from '../../../../shared/components/form-shell/form-shell.component';

@Component({
  selector: 'app-customer-view',
  templateUrl: './customer-view.component.html',
  styleUrls: ['./customer-view.component.css']
})
export class CustomerViewComponent implements OnInit {
  customer: Customer | null = null;
  isLoading = false;
  error: string | null = null;
  attachments: CustomerAttachment[] = [];
  isLoadingAttachments = false;

  activeTab: string = 'identification';
  readonly availableTabs: FormShellTab[] = [
    { id: 'identification', label: 'Identificação', icon: 'fas fa-building', required: true },
    { id: 'contact', label: 'Contato', icon: 'fas fa-address-book', required: true },
    { id: 'status', label: 'Status e Observações', icon: 'fas fa-clipboard-list', required: false },
    { id: 'payments', label: 'Dados Bancários', icon: 'fas fa-credit-card', required: false },
    { id: 'documents', label: 'Documentos', icon: 'fas fa-paperclip', required: false }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private customersService: CustomersService
  ) {}

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const id = idParam ? +idParam : 0;
    if (!id) {
      this.router.navigate(['/commercial/customers']);
      return;
    }
    this.loadCustomer(id);
  }

  loadCustomer(id: number): void {
    this.isLoading = true;
    this.error = null;
    this.customersService.getById(id).subscribe({
      next: (c) => {
        this.customer = c;
        this.isLoading = false;
        this.loadAttachments(c.id);
      },
      error: (err) => {
        this.error = err?.message || 'Erro ao carregar cliente.';
        this.isLoading = false;
      }
    });
  }

  loadAttachments(customerId: number): void {
    if (!customerId) {
      this.attachments = [];
      return;
    }
    this.isLoadingAttachments = true;
    this.customersService.listAttachments(customerId).subscribe({
      next: (list) => {
        this.attachments = list || [];
        this.isLoadingAttachments = false;
      },
      error: () => {
        this.isLoadingAttachments = false;
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/commercial/customers']);
  }

  goToEdit(): void {
    if (this.customer?.id) {
      this.router.navigate(['/commercial/customers', this.customer.id, 'edit']);
    }
  }

  selectTab(tabId: string): void {
    this.activeTab = tabId;
  }

  get isPF(): boolean {
    return this.customer?.personType === 'Fisica';
  }

  formatDocument(value: string | undefined): string {
    if (!value) return '-';
    const digits = value.replace(/\D/g, '');
    if (digits.length === 11) {
      return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    if (digits.length === 14) {
      return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return value;
  }

  formatPhone(value: string | undefined): string {
    if (!value) return '-';
    const digits = value.replace(/\D/g, '');
    if (digits.length === 11) {
      return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    if (digits.length === 10) {
      return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return value;
  }

  formatDate(value: string | undefined): string {
    if (!value) return '-';
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString('pt-BR');
  }

  display(value: string | number | boolean | null | undefined, fallback = '-'): string {
    if (value === undefined || value === null || value === '') return fallback;
    if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
    return String(value);
  }

  get displayContacts(): CustomerContact[] {
    if (this.customer?.contacts && this.customer.contacts.length > 0) {
      return [...this.customer.contacts].sort((a, b) => Number(!!b.isDefault) - Number(!!a.isDefault));
    }
    if (this.customer?.primaryPhone) {
      return [{
        contactType: 'Principal',
        contactName: this.customer.contactPersonName || this.customer.name,
        phoneNumber: this.customer.primaryPhone,
        secondaryPhoneNumber: this.customer.secondaryPhone,
        email: this.customer.email,
        isDefault: true
      }];
    }
    return [];
  }
}
